import { create } from 'zustand';
import type { ColorMode, CameraType } from '../types';
import { loadPersistedState, savePersistedState } from '../lib/persist';

export type ViewMode = 'map' | '3d' | 'split';

interface UiState {
  activeLayer: ColorMode;
  cameraType: CameraType;
  viewMode: ViewMode;
  showHud: boolean;
  isLoading: boolean;
  uploadError: string | null;
  isUploading: boolean;
  uploadProgress: number;
  showProjectPanel: boolean;
  showLayerPanel: boolean;
  // Envelope
  envWidth: number;
  envLength: number;
  envHeight: number;
  envRotation: number;
  envSetback: number;
  envX: number;
  envY: number;
  showEnvelope: boolean;
  showMedoids: boolean;
  showLasers: boolean;
  showDrainage: boolean;
  showSetback: boolean;
  showGrid: boolean;
  resetCameraNonce: number;

  setActiveLayer: (layer: ColorMode) => void;
  setCameraType: (camera: CameraType) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleHud: () => void;
  setLoading: (loading: boolean) => void;
  setUploadError: (error: string | null) => void;
  setUploading: (uploading: boolean) => void;
  setUploadProgress: (progress: number) => void;
  toggleProjectPanel: () => void;
  toggleLayerPanel: () => void;
  setShowProjectPanel: (show: boolean) => void;
  setShowLayerPanel: (show: boolean) => void;
  setEnvWidth: (w: number) => void;
  setEnvLength: (l: number) => void;
  setEnvHeight: (h: number) => void;
  setEnvRotation: (r: number) => void;
  setEnvSetback: (s: number) => void;
  setEnvX: (x: number) => void;
  setEnvY: (y: number) => void;
  setShowEnvelope: (show: boolean) => void;
  setShowMedoids: (show: boolean) => void;
  setShowLasers: (show: boolean) => void;
  setShowDrainage: (show: boolean) => void;
  setShowSetback: (show: boolean) => void;
  toggleGrid: () => void;
  resetCamera: () => void;
}

const defaults = {
  activeLayer: 'elevation' as ColorMode,
  cameraType: 'orbit' as CameraType,
  viewMode: 'map' as ViewMode,
  showHud: true,
  isLoading: false,
  uploadError: null as string | null,
  isUploading: false,
  uploadProgress: 0,
  showProjectPanel: true,
  showLayerPanel: true,
  envWidth: 2.4,
  envLength: 3.2,
  envHeight: 2.0,
  envRotation: 0,
  envSetback: 1.0,
  envX: 0,
  envY: 0,
  showEnvelope: false,
  showMedoids: false,
  showLasers: false,
  showDrainage: false,
  showSetback: false,
  showGrid: true,
  resetCameraNonce: 0,
};

const persisted = loadPersistedState();
const initial: typeof defaults = persisted
  ? {
      ...defaults,
      viewMode: (persisted.viewMode as ViewMode) ?? defaults.viewMode,
      cameraType: (persisted.cameraType as CameraType) ?? defaults.cameraType,
      activeLayer: (persisted.activeLayer as ColorMode) ?? defaults.activeLayer,
      showProjectPanel: persisted.showProjectPanel ?? defaults.showProjectPanel,
      showLayerPanel: persisted.showLayerPanel ?? defaults.showLayerPanel,
      showHud: persisted.showHud ?? defaults.showHud,
      envWidth: persisted.envWidth ?? defaults.envWidth,
      envLength: persisted.envLength ?? defaults.envLength,
      envHeight: persisted.envHeight ?? defaults.envHeight,
      envRotation: persisted.envRotation ?? defaults.envRotation,
      envSetback: persisted.envSetback ?? defaults.envSetback,
      envX: persisted.envX ?? defaults.envX,
      envY: persisted.envY ?? defaults.envY,
      showEnvelope: persisted.showEnvelope ?? defaults.showEnvelope,
      showMedoids: persisted.showMedoids ?? defaults.showMedoids,
      showLasers: persisted.showLasers ?? defaults.showLasers,
      showDrainage: persisted.showDrainage ?? defaults.showDrainage,
      showSetback: persisted.showSetback ?? defaults.showSetback,
      showGrid: persisted.showGrid ?? defaults.showGrid,
      isLoading: false,
      uploadError: null,
      isUploading: false,
      uploadProgress: 0,
      resetCameraNonce: 0,
    }
  : defaults;

export const useUiStore = create<UiState>((set) => ({
  ...initial,

  setActiveLayer: (layer) => {
    set({ activeLayer: layer });
    savePersistedState({ activeLayer: layer });
  },

  setCameraType: (camera) => {
    set({ cameraType: camera });
    savePersistedState({ cameraType: camera });
  },

  setViewMode: (mode) => {
    set({ viewMode: mode });
    savePersistedState({ viewMode: mode });
  },

  toggleHud: () =>
    set((state) => {
      const next = !state.showHud;
      savePersistedState({ showHud: next });
      return { showHud: next };
    }),

  setLoading: (loading) => set({ isLoading: loading }),

  setUploadError: (error) => set({ uploadError: error }),

  setUploading: (uploading) => set({ isUploading: uploading }),

  setUploadProgress: (progress) => set({ uploadProgress: progress }),

  toggleProjectPanel: () =>
    set((state) => {
      const next = !state.showProjectPanel;
      savePersistedState({ showProjectPanel: next });
      return { showProjectPanel: next };
    }),

  toggleLayerPanel: () =>
    set((state) => {
      const next = !state.showLayerPanel;
      savePersistedState({ showLayerPanel: next });
      return { showLayerPanel: next };
    }),

  setShowProjectPanel: (show) => {
    set({ showProjectPanel: show });
    savePersistedState({ showProjectPanel: show });
  },

  setShowLayerPanel: (show) => {
    set({ showLayerPanel: show });
    savePersistedState({ showLayerPanel: show });
  },

  setEnvWidth: (w) => {
    const clamped = Math.max(0.1, Math.min(50, w));
    set({ envWidth: clamped });
    savePersistedState({ envWidth: clamped });
  },
  setEnvLength: (l) => {
    const clamped = Math.max(0.1, Math.min(50, l));
    set({ envLength: clamped });
    savePersistedState({ envLength: clamped });
  },
  setEnvHeight: (h) => {
    const clamped = Math.max(0.1, Math.min(20, h));
    set({ envHeight: clamped });
    savePersistedState({ envHeight: clamped });
  },
  setEnvRotation: (r) => {
    const normalized = ((r % 360) + 360) % 360;
    set({ envRotation: normalized });
    savePersistedState({ envRotation: normalized });
  },
  setEnvSetback: (s) => {
    const clamped = Math.max(0, Math.min(20, s));
    set({ envSetback: clamped });
    savePersistedState({ envSetback: clamped });
  },
  setEnvX: (x) => {
    set({ envX: x });
    savePersistedState({ envX: x });
  },
  setEnvY: (y) => {
    set({ envY: y });
    savePersistedState({ envY: y });
  },
  setShowEnvelope: (show) => {
    set({ showEnvelope: show });
    savePersistedState({ showEnvelope: show });
  },
  setShowMedoids: (show) => {
    set({ showMedoids: show });
    savePersistedState({ showMedoids: show });
  },
  setShowLasers: (show) => {
    set({ showLasers: show });
    savePersistedState({ showLasers: show });
  },
  setShowDrainage: (show) => {
    set({ showDrainage: show });
    savePersistedState({ showDrainage: show });
  },
  setShowSetback: (show) => {
    set({ showSetback: show });
    savePersistedState({ showSetback: show });
  },
  toggleGrid: () =>
    set((state) => {
      const next = !state.showGrid;
      savePersistedState({ showGrid: next });
      return { showGrid: next };
    }),
  resetCamera: () => set((state) => ({ resetCameraNonce: state.resetCameraNonce + 1 })),
}));
